import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';

import { APP_ENV_CONFIG } from '../../../../common/config/app-config';
import { ApiResponseDto } from '../../../../common/dtos/api-response.dto';
import { silentContext } from '../../../../common/http/silent-request.context';
import { AuthenticationService } from '../../../../common/services/authentication/authentication.service';
import { LogFactory } from '../../../../common/services/logger/log-factory';
import { AppRefreshable, RefreshPolicy } from '../../../core/visibility/app-refresh';
import { RefreshKey } from '../../../core/visibility/refresh-key.enum';

/** What the master-switch endpoint answers. */
export interface PublishingAvailability {
  publishingEnabled: boolean;
}

/** '1' | '0' — the last verified answer, so a returning user is never guessed at. */
const CACHE_KEY = 'publishing.enabled';
/** Age after which the menu or the publisher route guard re-asks. */
const PUBLISHING_AVAILABILITY_STALE_AFTER_MS = 30 * 60 * 1000;

/**
 * Whether community publishing is live in this deployment (ADR-0226).
 *
 * The switch is server-owned and read from `GET /marketplace/availability`, not
 * copied into the app's env config. A build-time duplicate of a server flag is
 * exactly what the payment config warns against: the app would need a release to
 * agree with the api, and the two would drift the first time one moved without
 * the other.
 *
 * **Fails CLOSED on a cold cache**, like the purchase check and unlike the
 * country check. Nothing is lost by starting closed — publishing needs the
 * network anyway, so a user who cannot reach the api could not have published —
 * whereas starting open is the failure the user actually saw: a publish button
 * that opens a sheet the server then refuses.
 *
 * The endpoint needs a session, so the check is tied to one. The token is
 * restored asynchronously from native secure storage, and a launch-time ask
 * raced that restore and 401'd — the same fault that hid Help & Support from the
 * menu on a cold start. The localStorage cache masked it here from the second
 * launch onward, so it only bit on a first run after install; re-asking on every
 * session change removes the race rather than relying on the cache to cover it.
 *
 * Registered LAZY (ADR-0264): a resume only marks it stale, and the menu (where
 * My listings is drawn) and the publisher route guard pull a fresh answer when
 * they are next opened.
 */
@Injectable({ providedIn: 'root' })
export class PublishingAvailabilityService implements AppRefreshable {
  readonly refreshKey = RefreshKey.PublishingAvailability;
  readonly staleAfterMs = PUBLISHING_AVAILABILITY_STALE_AFTER_MS;
  readonly refreshPolicy = RefreshPolicy.Lazy;

  private readonly http = inject(HttpClient);
  private readonly envConfig = inject(APP_ENV_CONFIG);
  private readonly logger = inject(LogFactory).createLogger(
    PublishingAvailabilityService,
  );

  private readonly authService = inject(AuthenticationService);

  private inFlight: Promise<boolean | null> | null = null;

  private readonly origin = this.envConfig.backend.apiWebEndpoint;

  /** True only once the server has said so, or a previous run cached that it did. */
  readonly publishingEnabled = signal<boolean>(this.readCache() ?? false);

  /**
   * False until a live or cached answer exists. Screens that would otherwise
   * flash a publish button and retract it gate on this too; on a cold start
   * `publishingEnabled` is a safe assumption rather than a known fact.
   */
  readonly resolved = signal<boolean>(this.readCache() !== null);

  constructor() {
    // Signing out does NOT close publishing: the cached answer is a property of
    // the deployment, not of the user, and clearing it would make the next
    // sign-in flash a retracted publish button. Only a live answer moves it.
    this.authService
      .userId$()
      .pipe(takeUntilDestroyed())
      .subscribe((userId) => {
        if (userId) {
          void this.refresh();
        }
      });
  }

  /**
   * Ask the server. Called at launch, on a session change and when the menu or
   * a publisher route opens, so a switch flipped mid session takes effect
   * without the user reinstalling.
   *
   * Never throws and never toasts: an unreachable api is an expected,
   * self-healing state, and the last verified answer beats a fresh failure.
   */
  async refresh(): Promise<boolean> {
    return (await this.request()) ?? this.publishingEnabled();
  }

  /** The registry path: rejects when the server could not be asked. */
  async refreshNow(): Promise<void> {
    if ((await this.request()) === null) {
      throw new Error('Publishing availability refresh did not complete');
    }
  }

  private request(): Promise<boolean | null> {
    // Concurrent callers share one request. Launch, the session subscription
    // above and a screen's pull can all land together, and each used to issue
    // its own call — with no ordering guarantee between their answers.
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  /** The answer, or null when the server could not be read. */
  private async load(): Promise<boolean | null> {
    // Asking before the token restore finishes is a guaranteed 401; callers that
    // fire at launch reach this first.
    await firstValueFrom(this.authService.whenInitialized());
    if (!this.authService.getUserId()) {
      return this.publishingEnabled();
    }

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponseDto<PublishingAvailability>>(
          `${this.origin}/marketplace/availability`,
          { context: silentContext() },
        ),
      );
      const enabled = response?.data?.publishingEnabled;
      if (typeof enabled !== 'boolean') {
        throw new Error('Publishing availability is missing from the response.');
      }

      this.publishingEnabled.set(enabled);
      this.resolved.set(true);
      localStorage.setItem(CACHE_KEY, enabled ? '1' : '0');
      return enabled;
    } catch (error) {
      // Debug, not error: offline and cold-start-before-login are normal, and
      // the consequence (publishing stays hidden) is the safe outcome rather
      // than a failure the user asked for.
      this.logger.debug(
        'Publishing availability check failed; keeping the last known answer',
        error,
      );
      return null;
    }
  }

  private readCache(): boolean | null {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return null;
  }
}
